import { useEffect, useMemo, useReducer, useRef } from 'react';
import { useQuery } from '@apollo/client';

import { CRM_CONTACT_LIST } from './contacts.queries';
import {
  buildFilter,
  buildSort,
  columnById,
  ContactListState,
  CrmContactFilter,
  CrmContactSort,
  CrmContactTotal,
  errorCodeOf,
  errorFieldOf,
} from './contactList';

/** The row as CRM_CONTACT_LIST selects it. The related lists are there only while their column is shown. */
export interface ContactRow {
  id: number;
  fullName: string | null;
  primaryEmail: string | null;
  primaryPhone: string | null;
  city: string | null;
  region: string | null;
  countryCode: string | null;
  canEmail: boolean;
  canCall: boolean;
  canText: boolean;
  doNotCall: boolean;
  doNotContact: boolean;
  callCount: number;
  lastCalledAt: string | null;
  lastContactedAt: string | null;
  supportLevel: number | null;
  lifecycleStage: string | null;
  source: string;
  nbCreatedAt: string | null;
  createdAt: string;
  /** NationBuilder sign-up if migrated, else when the record was added (card 18). */
  joinedAt: string;
  matchedOn: Array<string> | null;
  tags?: Array<{ tagId: number; name: string; color: string | null }>;
  owner?: { id: number; firstName: string; lastName: string } | null;
  engagement?: { emailsSent: number; engagementRate: number | null } | null;
}

interface ContactPageData {
  crmContacts: {
    items: Array<ContactRow>;
    skip: number;
    take: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
    endCursor: string | null;
    total?: CrmContactTotal & { exactLimit: number };
  };
}

/** The part of a request fixed the first time a page is asked for. */
interface PageRequest {
  filter?: CrmContactFilter;
  sort?: Array<CrmContactSort>;
  take: number;
  skip?: number;
  after?: string;
  withTotal: boolean;
}

/**
 * One page of the contact list, asked the way CRM_CONTACT_QUERY_FRONTEND.md §16
 * says:
 *
 * - **Next and previous step by cursor** (`after`), which costs the same at any
 *   depth. A page reached any other way (a link, a reload, the way back from a
 *   contact, "last page") is asked by offset (`skip`).
 * - **`total` once per filter.** Paging and sorting do not change it.
 * - **A page's request is fixed the first time it is asked.** Coming back to it
 *   (previous, or back from a contact) sends identical variables, so Apollo
 *   answers from its cache instead of the API.
 */
export const useContactPage = (state: ContactListState, shownColumns: ReadonlyArray<string>) => {
  const filter = useMemo(() => buildFilter(state), [state]);
  const sort = useMemo(() => buildSort(state), [state]);

  const filterKey = JSON.stringify(filter ?? null);
  const listKey = JSON.stringify([filterKey, sort ?? null, state.pageSize]);
  const requestKey = `${listKey}#${state.page}`;

  const memory = useRef({
    /** Per filter, sort and page size: the `after` that reaches each page. */
    cursors: new Map<string, Map<number, string>>(),
    requests: new Map<string, PageRequest>(),
    totals: new Map<string, CrmContactTotal>(),
  });
  const [, forget] = useReducer((n: number) => n + 1, 0);

  const cursorsFor = (key: string) => {
    let cursors = memory.current.cursors.get(key);
    if (!cursors) {
      cursors = new Map();
      memory.current.cursors.set(key, cursors);
    }
    return cursors;
  };

  let request = memory.current.requests.get(requestKey);
  if (!request) {
    const after = state.page > 0 ? cursorsFor(listKey).get(state.page) : undefined;
    request = {
      ...(filter ? { filter } : {}),
      ...(sort ? { sort } : {}),
      take: state.pageSize,
      ...(after ? { after } : { skip: state.page * state.pageSize }),
      withTotal: !memory.current.totals.has(filterKey),
    };
    memory.current.requests.set(requestKey, request);
  }

  const needs = new Set(shownColumns.map((id) => columnById(id)?.needs).filter(Boolean));

  const { data, previousData, loading, error } = useQuery<ContactPageData>(CRM_CONTACT_LIST, {
    variables: {
      ...request,
      withTags: needs.has('tags'),
      withOwner: needs.has('owner'),
      withEngagement: needs.has('engagement'),
    },
    // A search's `matchedOn` is stored on the shared, cached contact. Asked
    // fresh each time so a page revisited after another search shows its own.
    fetchPolicy: state.search ? 'network-only' : 'cache-first',
  });

  const page = data?.crmContacts;

  useEffect(() => {
    if (!page) return;
    if (page.total) memory.current.totals.set(filterKey, page.total);
    if (page.hasNextPage && page.endCursor) cursorsFor(listKey).set(state.page + 1, page.endCursor);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  // A cursor the API no longer accepts (§15): drop it and ask by offset instead.
  const staleCursor = Boolean(request.after) && errorCodeOf(error) === 'CRM_INVALID_QUERY' && errorFieldOf(error) === 'after';
  useEffect(() => {
    if (!staleCursor) return;
    memory.current.requests.delete(requestKey);
    memory.current.cursors.delete(listKey);
    forget();
  }, [staleCursor, requestKey, listKey]);

  const shown = page ?? previousData?.crmContacts;

  return {
    filter,
    rows: shown?.items ?? [],
    /** True while a new page is on its way; the previous rows stay on screen meanwhile. */
    loading,
    error: staleCursor ? undefined : error,
    hasNextPage: Boolean(page?.hasNextPage),
    total: page?.total ?? memory.current.totals.get(filterKey),
  };
};
